import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, createStyles, Menu, Stack, Switch, Text, Tooltip, UnstyledButton} from '@mantine/core';
import React from 'react';

import ArchiveModal from 'archive/components/modals/ArchiveModal';
import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {SkillSummary} from 'llm/chat-library/skills/SkillsDAO';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';

interface SkillsListProps {
  skills: Array<SkillSummary>;
  isTenantAdmin: boolean;
  onArchive: (skillId: string, reason: string) => void;
  onManageAccess?: (skill: SkillSummary) => void;
  onTryWithChat?: (skill: SkillSummary) => void;
  onEditWithChat?: (skill: SkillSummary) => void;
  onEnable: (skillId: string) => void;
  onDisable: (skillId: string) => void;
  selectedSkillId?: string | null;
  onSelectSkill: (skill: SkillSummary) => void;
}

const useListStyles = createStyles(() => ({
  list: {
    overflowY: 'auto',
    flex: '1 1 auto',
    minHeight: 0,
  },
}));

const useCardStyles = createStyles((theme, {isSelected}: {isSelected: boolean}) => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.sm} 10px`,
    borderRadius: theme.radius.sm,
    border: `1px solid ${isSelected ? theme.colors.blue[4] : theme.colors.gray[3]}`,
    backgroundColor: isSelected ? theme.colors.blue[0] : theme.white,
    width: '100%',
    textAlign: 'left',
    ':hover': {
      borderColor: theme.colors.blue[3],
      backgroundColor: isSelected ? theme.colors.blue[0] : theme.colors.gray[0],
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    flexWrap: 'wrap',
  },
  description: {
    marginTop: 2,
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
    marginTop: 2,
  },
}));

function SkillCard({
  skill,
  isTenantAdmin,
  onArchiveClick,
  onManageAccessClick,
  onTryWithChatClick,
  onEditWithChatClick,
  onToggleEnabled,
  isSelected,
  onSelect,
}: {
  skill: SkillSummary;
  isTenantAdmin: boolean;
  onArchiveClick: (skill: SkillSummary) => void;
  onManageAccessClick?: (skill: SkillSummary) => void;
  onTryWithChatClick?: (skill: SkillSummary) => void;
  onEditWithChatClick?: (skill: SkillSummary) => void;
  onToggleEnabled: (skill: SkillSummary, enabled: boolean) => void;
  isSelected: boolean;
  onSelect: (skill: SkillSummary) => void;
}): JSX.Element {
  const canArchive = skill.extensionDefinitionId == null && (skill.canManageAccess || isTenantAdmin);
  const canManageAccess =
    onManageAccessClick != null && skill.canManageAccess && skill.extensionDefinitionId == null;
  const {classes} = useCardStyles({isSelected});

  return (
    <UnstyledButton
      component="div"
      role="button"
      tabIndex={0}
      className={classes.card}
      aria-label={`View details for ${skill.name}`}
      aria-pressed={isSelected}
      onClick={() => {
        onSelect(skill);
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(skill);
        }
      }}
    >
      <div className={classes.cardHeader}>
        <div className={classes.cardContent}>
          <div className={classes.nameRow}>
            <Text fw="bold" size="sm" truncate={true}>
              {skill.name}
            </Text>
          </div>
          {skill.creatorName && (
            <Text size="xs" c="dimmed">
              by {skill.creatorName}
            </Text>
          )}
          <Text size="xs" c="dimmed" lineClamp={2} className={classes.description}>
            {skill.description}
          </Text>
        </div>
        <div
          className={classes.actionsContainer}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
          }}
        >
          <Tooltip
            label={
              skill.isEnabled
                ? 'Disable skill (agents will not use it in your sessions)'
                : 'Enable skill (agents will use it in your sessions)'
            }
            withinPortal={true}
          >
            <Switch
              checked={skill.isEnabled}
              size="xs"
              aria-label={skill.isEnabled ? `Disable ${skill.name}` : `Enable ${skill.name}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onToggleEnabled(skill, e.currentTarget.checked);
              }}
              {...getTestProps({component: 'SkillsList', element: 'toggleSkillEnabled'})}
            />
          </Tooltip>
          <Menu position="bottom-end" withinPortal={true}>
            <Menu.Target>
              <ActionIcon
                size="sm"
                variant="subtle"
                aria-label="More options"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                }}
                {...getTestProps({component: 'SkillsList', element: 'moreOptionsButton'})}
              >
                <BenchlingIcon icon="ellipsis-vertical" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onTryWithChatClick != null && (
                <Menu.Item
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onTryWithChatClick(skill);
                  }}
                  {...getTestProps({component: 'SkillsList', element: 'tryWithChatItem'})}
                >
                  Try with chat
                </Menu.Item>
              )}
              {onEditWithChatClick != null && (
                <Menu.Item
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onEditWithChatClick(skill);
                  }}
                  {...getTestProps({component: 'SkillsList', element: 'editWithChatItem'})}
                >
                  Edit with chat
                </Menu.Item>
              )}
              {canManageAccess && (
                <Menu.Item
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onManageAccessClick?.(skill);
                  }}
                  {...getTestProps({component: 'SkillsList', element: 'manageAccessItem'})}
                >
                  Manage access
                </Menu.Item>
              )}
              {canArchive && (
                <Menu.Item
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onArchiveClick(skill);
                  }}
                  {...getTestProps({component: 'SkillsList', element: 'archiveSkillItem'})}
                >
                  Archive
                </Menu.Item>
              )}
              <Menu.Item
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  exportAuditLogs([skill.id]);
                }}
                {...getTestProps({component: 'SkillsList', element: 'exportAuditLogItem'})}
              >
                {EXPORT_AUDIT_LOG_DISPLAY_STRING}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </div>
    </UnstyledButton>
  );
}

export default function SkillsList({
  skills,
  isTenantAdmin,
  onArchive,
  onManageAccess,
  onTryWithChat,
  onEditWithChat,
  onEnable,
  onDisable,
  selectedSkillId,
  onSelectSkill,
}: SkillsListProps): JSX.Element {
  const {classes} = useListStyles();

  if (skills.length === 0) {
    return (
      <Text fw="normal" c="dimmed" size="sm">
        No skills available yet.
      </Text>
    );
  }

  function handleArchiveClick(skill: SkillSummary): void {
    legacyRunModal(ArchiveModal, {
      items: [{id: skill.id, name: skill.name}],
      onArchive: (itemIds: Array<string>, purpose: string) => {
        for (const id of itemIds) {
          onArchive(id, purpose);
        }
      },
    });
  }

  function handleToggleEnabled(skill: SkillSummary, enabled: boolean): void {
    if (enabled) {
      onEnable(skill.id);
    } else {
      onDisable(skill.id);
    }
  }

  function handleSelectSkill(skill: SkillSummary): void {
    onSelectSkill(skill);
  }

  return (
    <Stack spacing="md" className={classes.list}>
      {skills.map((skill) => (
        <SkillCard
          key={skill.id}
          skill={skill}
          isTenantAdmin={isTenantAdmin}
          onArchiveClick={handleArchiveClick}
          onManageAccessClick={onManageAccess}
          onTryWithChatClick={onTryWithChat}
          onEditWithChatClick={onEditWithChat}
          onToggleEnabled={handleToggleEnabled}
          isSelected={selectedSkillId === skill.id}
          onSelect={handleSelectSkill}
        />
      ))}
    </Stack>
  );
}
